import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./FileManager.module.css";

import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { showBasicAlert } from "../../../core/common/useAlert";
import api from "../../../services/api";
import CommonSelect from "../../../core/common/commonSelect";
import ImageSelectDropMenu from "../../administration/imageSelectDropMenu";
import { getEmployeeImageUrl } from "../../../services/assetCommonService";
import DeleteModal from "../../../core/modals/deleteModal";

import {
  FileListView,
  FileGridView,
  FilePreview,
  LoadMoreToggle,
  FileModal,
  ShareDetailsModal,
  RecycleBinView,
  ImageLightbox,
} from "./components";
import type { RecycleBinItem } from "./components/RecycleBinView";
import {
  formatSize,
  getFriendlyFileType,
  getIconByMime,
  isPreviewable,
} from "./helpers";
import { EMPTY_FILE_COUNT } from "./constants";
import type {
  DriveItem,
  SharedFileItem,
  FileTypeCount,
  FileViewType,
  SortState,
  EditState,
  SelectionState,
  DragState,
  FileActionsConfig,
} from "./types"

const FileManager = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedId, setSelectedId] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [quickAccessItems, setQuickAccessItems] = useState<DriveItem[]>([]);
  const [originalQuickAccessItems, setOriginalQuickAccessItems] = useState<
    DriveItem[]
  >([]);
  const [shareAccessItem, setShareAccessItem] = useState<DriveItem[]>([]);
  const [originalShareAccessItems, setOriginalShareAccessItems] = useState<
    SharedFileItem[]
  >([]);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editName, setEditName] = useState<string>("");
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [folderStack, setFolderStack] = useState<number[]>([]);
  const [employeeDetail, setEmployeeDetail] = useState<any>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [fileCounts, setFileCounts] = useState<{
    myfolder: FileTypeCount;
    shared: FileTypeCount;
    official: FileTypeCount;
  }>({
    myfolder: { ...EMPTY_FILE_COUNT },
    shared: { ...EMPTY_FILE_COUNT },
    official: { ...EMPTY_FILE_COUNT },
  });
  const [currentView, setCurrentView] = useState<FileViewType>("myfolder");
  const [showShareModal, setShowShareModal] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
  const [storageInfo, setStorageInfo] = useState<{
    used_storage: number;
    max_storage: number;
  }>({ used_storage: 0, max_storage: 1 });
  const [showAllFolders, setShowAllFolders] = useState(true);
  const [showSharedWithMe, setShowSharedWithMe] = useState(false);
  const [showOfficialDocuments, setShowOfficialDocuments] = useState(false);
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [recycleBinItems, setRecycleBinItems] = useState<RecycleBinItem[]>([]);
  const [itemsToPurge, setItemsToPurge] = useState<RecycleBinItem[]>([]);
  const [officialDocuments, setOfficialDocuments] = useState<DriveItem[]>([]);
  const [originalOfficialDocuments, setOriginalOfficialDocuments] = useState<
    DriveItem[]
  >([]);
  const [panelSize] = useState(9);
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [sortColumn, setSortColumn] = useState<string>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [selectedCompany, setSelectedCompany] = useState({
    companyId: null,
    companyName: "",
    companyType: "",
  });
  const [activeFileType, setActiveFileType] = useState<string | null>(null);
  const [filteredEmployees, setFilteredEmployees] = useState<any[]>([]);
  const [previewModal, setPreviewModal] = useState<{
    show: boolean;
    item: DriveItem | null;
  }>({ show: false, item: null });
  const [lightbox, setLightbox] = useState<{
    show: boolean;
    images: DriveItem[];
    index: number;
  }>({ show: false, images: [], index: 0 });
  const [selectedItems, setSelectedItems] = useState<DriveItem[]>([]);
  const [cutItems, setCutItems] = useState<DriveItem[]>([]);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    type: "item" | "folder";
    targetItem?: DriveItem;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverItem, setDragOverItem] = useState<number | null>(null);
  const [dragOverFolder, setDragOverFolder] = useState(false);
  const [isFileDragging, setIsFileDragging] = useState(false);
  const currentUser = useSelector((state: any) => state.user.user);
  const signature = localStorage.getItem("signature");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [itemsToDelete, setItemsToDelete] = useState<DriveItem[]>([]);
  const [showShareDetailsModal, setShowShareDetailsModal] = useState(false);
  const [selectedFileForShareDetails, setSelectedFileForShareDetails] =
    useState<DriveItem | null>(null);
  const [selectedFileShareInfo, setSelectedFileShareInfo] = useState<any>(null);

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    const handleScroll = () => setContextMenu(null);

    if (contextMenu) {
      document.addEventListener("click", handleClick);
      document.addEventListener("scroll", handleScroll);
    }

    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("scroll", handleScroll);
    };
  }, [contextMenu]);

  const fetchUI = async () => {
    try {
      const [employeeDetails, getAllCompanyList] = await Promise.all([
        api.post("userService/getActiveEmployeesListData", { signature }),
        api.post("userService/getAllCompanyList", { signature }),
      ]);

      setEmployeeDetail(employeeDetails.data?.data?.data?.allUsers || []);
      setCompanies(getAllCompanyList?.data?.data?.data);
    } catch (err: any) {
      showBasicAlert(err.data.message[0].msg);
      console.error("Error fetching dashboard data:", err);
    }
  };

  const fetchInitialData = async () => {
    try {
      const [
        quickAccessResponse,
        storageResponse,
        sharedFilesResponse,
        getFileTypeCounts,
        getSharedFileTypeCounts,
        getOfficialFileTypeCounts,
      ] = await Promise.all([
        api.post("driveService/getFirstFile", { signature }),
        api.post("driveService/getUserStorageLimit", { signature }),
        api.post("driveService/getPermissionedFileByUserId", { signature }),
        api.post("driveService/getFileTypeCounts", { signature }),
        api.post("driveService/getSharedFileTypeCounts", { signature }),
        api.post("driveService/getOfficialFileTypeCounts", { signature }),
      ]);

      const quickAccessData =
        quickAccessResponse?.data?.message?.[0]?.data || [];
      setQuickAccessItems(quickAccessData);
      setOriginalQuickAccessItems(quickAccessData);

      const storageItems = storageResponse?.data?.message?.[0]?.data;
      setStorageInfo({
        used_storage: parseFloat(storageItems.used_storage),
        max_storage: parseFloat(storageItems.max_storage),
      });

      const sharedItems = sharedFilesResponse?.data?.data?.data || [];
      const shareFile: DriveItem[] = sharedItems.map(
        (item: SharedFileItem) => item.file,
      );
      setShareAccessItem(shareFile);
      setOriginalShareAccessItems(sharedItems);
      setFileCounts({
        myfolder: getFileTypeCounts?.data?.data?.data || { ...EMPTY_FILE_COUNT },
        shared:
          getSharedFileTypeCounts?.data?.data?.data || { ...EMPTY_FILE_COUNT },
        official:
          getOfficialFileTypeCounts?.data?.data?.data || { ...EMPTY_FILE_COUNT },
      });
    } catch (err) {
      console.error("Error fetching initial data:", err);
    }
  };

  // Refresh only the storage bar and file-type counts without touching the
  // current folder listing (so the user stays in the folder they were in).
  const refreshStorageAndCounts = async () => {
    try {
      const [
        storageResponse,
        getFileTypeCounts,
        getSharedFileTypeCounts,
        getOfficialFileTypeCounts,
      ] = await Promise.all([
        api.post("driveService/getUserStorageLimit", { signature }),
        api.post("driveService/getFileTypeCounts", { signature }),
        api.post("driveService/getSharedFileTypeCounts", { signature }),
        api.post("driveService/getOfficialFileTypeCounts", { signature }),
      ]);

      const storageItems = storageResponse?.data?.message?.[0]?.data;
      if (storageItems) {
        setStorageInfo({
          used_storage: parseFloat(storageItems.used_storage),
          max_storage: parseFloat(storageItems.max_storage),
        });
      }

      setFileCounts({
        myfolder: getFileTypeCounts?.data?.data?.data || { ...EMPTY_FILE_COUNT },
        shared:
          getSharedFileTypeCounts?.data?.data?.data || { ...EMPTY_FILE_COUNT },
        official:
          getOfficialFileTypeCounts?.data?.data?.data || { ...EMPTY_FILE_COUNT },
      });
    } catch (err) {
      console.error("Error refreshing storage/counts:", err);
    }
  };

  const currentFileTypeCount = fileCounts[currentView];

  const getSectionTitle = () => {
    if (showAllFolders) return "All Folders / Files";
    if (showSharedWithMe) return "Shared with Me";
    if (showOfficialDocuments) return "Official Document";
    return "";
  };

  useEffect(() => {
    fetchUI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchInitialData();
    handleBack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showOfficialDocuments) {
      const fetchOfficialDocuments = async () => {
        try {
          const response = await api.post(
            "driveService/getUserAccessibleOfficialDocuments",
            { signature },
          );
          const officialDocs = response?.data?.data?.data || [];
          const official: DriveItem[] = officialDocs.map(
            (item: SharedFileItem) => item.file,
          );
          setOfficialDocuments(official);
          setOriginalOfficialDocuments(official);
        } catch (error) {
          console.error("Error fetching official documents:", error);
        }
      };
      fetchOfficialDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOfficialDocuments]);

  const companyChoose = [
    ...companies?.map((company: any) => ({
      value: company.id,
      label: company.name + " - " + company.type,
      companyName: company.name,
    })),
  ];

  const handleCompanyChange = (selected: any) => {
    const { value, label, companyName } = selected;
    const companyType = label.split(" - ")[1];

    setSelectedCompany({
      companyId: value,
      companyName,
      companyType,
    });

    const filtered = employeeDetail.filter(
      (emp: any) => emp.CompanyId === value && emp.CompanyType === companyType,
    );
    setFilteredEmployees(filtered);

    setStep(2);
  };

  const handleShare = async (userId: any) => {
    const data = {
      signature,
      file_id: selectedId.id,
      users: [
        {
          user_id: userId,
          access_type: "view",
        },
      ],
    };

    const response = await api.post(
      "driveService/shareFileWithPermissions",
      data,
    );

    if (response?.data?.message?.[0]?.status) {
      showBasicAlert("File shared successfully!");
      setShowShareModal(false);
      setSelectedEmployee(null);
      setStep(1);
      fetchCurrentFolderItems(currentFolderId);
    } else {
      showBasicAlert("Failed to share the file.");
    }
  };

  const handleCopy = async (item: DriveItem) => {
    const data = {
      signature,
      file_id: item.id,
    };

    try {
      const response = await api.post("driveService/getFileLink", data);

      if (response.status === 200 && response?.data?.message[0]?.data) {
        const fileLink = response.data.message[0].data.link;

        if (fileLink) {
          navigator.clipboard
            .writeText(fileLink)
            .then(() => {
              showBasicAlert("Link copied");
            })
            .catch((err) => {
              console.error("Failed to copy the link: ", err);
            });
        } else {
          showBasicAlert("Cant copy this link");
        }
      } else {
        console.error("Error: Link not found in response");
      }
    } catch (error) {
      console.error("Error in handleCopy:", error);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setSelectedFile(file);
      await handleUpload(file);

      // Reset the file input after handling
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleNewClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // triggers file input click
    }
  };

  const handleUpload = async (
    file: File,
    parentId: number | null = currentFolderId,
  ) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name);
      formData.append("type", "2"); // 2 = file
      formData.append("mime_type", file.type);
      if (parentId !== null) {
        formData.append("parent_id", parentId.toString());
      }
      formData.append("size", file.size.toString());
      formData.append("status", "1");
      formData.append("signature", signature || "");

      const response = await api.post("driveService/addDriveItem", formData);
      const result = response.data?.message?.[0];
      showBasicAlert(result?.msg || "File uploaded successfully.");

      await fetchCurrentFolderItems(currentFolderId);
      await refreshStorageAndCounts();
      setSelectedFile(null);
    } catch (error: any) {
      console.error("Upload error:", error);
      showBasicAlert(
        error?.response?.data?.message?.[0]?.msg || "Upload failed.",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleFolderClick = async (folder: DriveItem) => {
    try {
      const data = {
        signature: signature,
        parent_id: folder.id,
      };

      const response = await api.post("driveService/getAllDriveItems", data);
      const items = response.data.message[0].data;

      setQuickAccessItems(items);
      setOriginalQuickAccessItems(items);

      // Update the folder stack before setting the current folder ID
      setFolderStack((prev) => {
        const newStack = [...prev];
        // Only add to stack if it's a different folder
        if (newStack[newStack.length - 1] !== folder.id) {
          newStack.push(folder.id);
        }
        return newStack;
      });

      setCurrentFolderId(folder.id);
    } catch (error) {
      console.error("Failed to fetch folder contents:", error);
    }
  };

  const handleShowShareDetails = async (item: any) => {
    try {
      if (item.share_info) {
        setSelectedFileForShareDetails(item);
        setSelectedFileShareInfo(item.share_info);
        setShowShareDetailsModal(true);
      }
    } catch (error) {
      console.error("Error fetching share details:", error);
      showBasicAlert("Failed to load share details.");
    }
  };
  const handleDownloadFile = async (item: DriveItem) => {
    try {
      const data = {
        signature: signature,
        id: item.id,
      };
      const response = await api.post("driveService/downloadDriveItem", data, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", item.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  const handleBack = async () => {
    if (folderStack.length <= 1) {
      await fetchCurrentFolderItems(null);
      setFolderStack([]);
      setCurrentFolderId(null);
      return;
    }

    const newStack = [...folderStack];
    newStack.pop(); // Remove current folder
    const previousFolderId = newStack[newStack.length - 1] || null;

    await fetchCurrentFolderItems(previousFolderId);
    setFolderStack(newStack);
  };

  // Update the fetchCurrentFolderItems function to maintain the stack correctly
  const fetchCurrentFolderItems = async (parentId: number | null) => {
    const data: any = { signature };
    if (parentId !== null) {
      data.parent_id = Number(parentId);
    }

    try {
      const response = await api.post(
        parentId !== null
          ? "driveService/getAllDriveItems"
          : "driveService/getRootDriveItemsByOwner",
        data,
      );

      const items = response.data.message[0].data || [];
      setQuickAccessItems(items);
      setOriginalQuickAccessItems(items);
      setCurrentFolderId(parentId);

      // Update folder stack when fetching items
      if (parentId !== null) {
        setFolderStack((prev) => {
          const newStack = [...prev];
          if (newStack[newStack.length - 1] !== parentId) {
            newStack.push(parentId);
          }
          return newStack;
        });
      } else {
        setFolderStack([]);
      }
    } catch (error) {
      console.error("Failed to fetch folder items:", error);
      setQuickAccessItems([]);
      setOriginalQuickAccessItems([]);
    }
  };

  const handleEdit = async (item: DriveItem) => {
    setEditingItemId(item.id);
    setEditName(item.name);
  };

  const handleKeyDown = async (
    e: React.KeyboardEvent<HTMLInputElement>,
    item: DriveItem,
  ) => {
    if (e.key === "Enter") {
      try {
        const data = {
          signature: signature,
          id: item.id,
          name: editName,
          type: item.type,
        };

        const response = await api.post("driveService/updateDriveItem", data);
        const result = response.data.message[0];

        if (result.status) {
          await fetchCurrentFolderItems(currentFolderId);
          showBasicAlert("File/Folder renamed successfully.");
        } else {
          showBasicAlert("Failed to rename file/folder.");
        }

        setEditingItemId(null);
      } catch (error: any) {
        console.error("Rename failed:", error);
        showBasicAlert(
          error?.response?.data?.message?.[0]?.msg || "Rename failed.",
        );
      }
    }
  };

  const handleBlur = async (item: DriveItem) => {
    try {
      const data = {
        signature: signature,
        id: item.id,
        name: editName,
        type: item.type,
      };

      const response = await api.post("driveService/updateDriveItem", data);
      const result = response.data.message[0];

      if (result.status) {
        await fetchCurrentFolderItems(currentFolderId);
        showBasicAlert("File/Folder renamed successfully.");
      } else {
        showBasicAlert("Failed to rename file/folder.");
      }

      setEditingItemId(null);
    } catch (error: any) {
      console.error("Rename on blur failed:", error);
      showBasicAlert(
        error?.response?.data?.message?.[0]?.msg || "Rename failed.",
      );
    }
  };

  const handleDelete = async (items: DriveItem | DriveItem[]) => {
    const itemArray = Array.isArray(items) ? items : [items];
    setItemsToDelete(itemArray);
  };

  const handleConfirmDelete = async () => {
    try {
      const data = {
        signature,
        ids: itemsToDelete.map((item) => item.id),
      };

      const response = await api.post("driveService/deleteDriveItem", data);
      const deletedMessage = response.data.message[0].msg;
      showBasicAlert(deletedMessage);

      await refreshStorageAndCounts();

      const updatedItems = quickAccessItems.filter(
        (i) => !itemsToDelete.some((item) => item.id === i.id),
      );
      setQuickAccessItems(updatedItems);
      setOriginalQuickAccessItems(updatedItems);
      setSelectedItems([]); // Clear selected items after deletion

      if (updatedItems.length === 0) {
        setQuickAccessItems([]);
        setOriginalQuickAccessItems([]);
      } else {
        await fetchCurrentFolderItems(currentFolderId);
      }
    } catch (error) {
      console.error("Delete failed:", error);
      showBasicAlert("Failed to delete the item(s).");
    } finally {
      setItemsToDelete([]);
    }
  };

  const fetchRecycleBinItems = async () => {
    try {
      const response = await api.post("driveService/getRecycleBinItems", {
        signature,
      });
      const items = response?.data?.message?.[0]?.data || [];
      setRecycleBinItems(items);
    } catch (error) {
      console.error("Failed to fetch recycle bin items:", error);
      setRecycleBinItems([]);
    }
  };

  const handleRestore = async (items: RecycleBinItem[]) => {
    if (items.length === 0) return;
    try {
      const response = await api.post("driveService/restoreDriveItem", {
        signature,
        ids: items.map((item) => item.id),
      });
      const result = response?.data?.message?.[0];
      showBasicAlert(result?.msg || "Restored from Recycle Bin successfully.");
      await fetchRecycleBinItems();
      await refreshStorageAndCounts();
    } catch (error: any) {
      console.error("Restore failed:", error);
      showBasicAlert(
        error?.response?.data?.message?.[0]?.msg || "Failed to restore item(s).",
      );
    }
  };

  // Stage items for permanent deletion; the shared DeleteModal confirms.
  const handlePermanentDelete = (items: RecycleBinItem[]) => {
    if (items.length === 0) return;
    setItemsToPurge(items);
  };

  const handleConfirmPurge = async () => {
    if (itemsToPurge.length === 0) return;
    try {
      const response = await api.post(
        "driveService/permanentlyDeleteDriveItem",
        {
          signature,
          ids: itemsToPurge.map((item) => item.id),
        },
      );
      const result = response?.data?.message?.[0];
      showBasicAlert(result?.msg || "Item(s) deleted permanently.");
      await fetchRecycleBinItems();
      await refreshStorageAndCounts();
    } catch (error: any) {
      console.error("Permanent delete failed:", error);
      showBasicAlert(
        error?.response?.data?.message?.[0]?.msg ||
          "Failed to permanently delete item(s).",
      );
    } finally {
      setItemsToPurge([]);
    }
  };

  const handleSort = (column: string) => {
    const isSameColumn = sortColumn === column;
    const newDirection =
      isSameColumn && sortDirection === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortDirection(newDirection);
  };

  const sortedItems = (items: DriveItem[]) =>
    [...items].sort((a, b) => {
      if (!sortColumn || sortColumn === "default") {
        if (a.type === 1 && b.type !== 1) return -1;
        if (a.type !== 1 && b.type === 1) return 1;
      }

      let comparison = 0;

      if (sortColumn === "name") {
        comparison = (a.name || "").localeCompare(b.name || "");
      } else if (sortColumn === "date") {
        comparison =
          new Date(a.updatedAt || "").getTime() -
          new Date(b.updatedAt || "").getTime();
      } else if (sortColumn === "type") {
        const typeA =
          a.type === 1
            ? "Folder"
            : a.document_type
              ? a.document_type
              : a.mime_type || "File";
        const typeB =
          b.type === 1
            ? "Folder"
            : b.document_type
              ? b.document_type
              : b.mime_type || "File";
        comparison = typeA.localeCompare(typeB);
      } else if (sortColumn === "size") {
        comparison = Number(a.size) - Number(b.size);
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

  const handleFileTypeClick = (
    type: "image" | "video" | "pdf" | "other" | "folder" | "all",
  ) => {
    setActiveFileType(type);

    try {
      let itemsToFilter: any[] = [];

      if (currentView === "myfolder") {
        itemsToFilter = originalQuickAccessItems;
      } else if (currentView === "shared") {
        itemsToFilter = originalShareAccessItems.map((item) => item.file);
      } else if (currentView === "official") {
        itemsToFilter = originalOfficialDocuments.map((item: any) =>
          item.file ? item.file : item,
        );
      }

      const filteredItems = itemsToFilter.filter((item) => {
        if (type === "folder") {
          return item.type === 1;
        } else if (type === "image") {
          return item.mime_type?.startsWith("image/");
        } else if (type === "video") {
          return item.mime_type?.startsWith("video/");
        } else if (type === "pdf") {
          return item.mime_type === "application/pdf";
        } else if (type === "other") {
          return (
            item.type !== 1 &&
            !item.mime_type?.startsWith("image/") &&
            !item.mime_type?.startsWith("video/") &&
            item.mime_type !== "application/pdf"
          );
        }
        return false;
      });
      if (currentView === "myfolder") {
        setQuickAccessItems(filteredItems);
      } else if (currentView === "shared") {
        setShareAccessItem(filteredItems);
      } else if (currentView === "official") {
        setOfficialDocuments(filteredItems);
      }
    } catch (err) {
      console.error(`Failed to filter ${type} files`, err);
      showBasicAlert(`Failed to filter ${type} files`);
    }
  };

  const routes = all_routes;

  const usagePercent = Math.min(
    (storageInfo.used_storage / storageInfo.max_storage) * 100,
    100,
  );

  const [visibleCounts, setVisibleCounts] = useState<any>({
    quickAccess: 10,
    shared: 10,
    official: 10,
  });

  const totalItems = sortedItems(quickAccessItems).length;
  const totalSharedItems = sortedItems(shareAccessItem).length;
  const totalOfficialItems = sortedItems(officialDocuments).length;

  const handleFileDoubleClick = (item: DriveItem) => {
    if (item.type === 1) {
      handleFolderClick(item);
      return;
    }

    // Images open in the Picasa-style lightbox, paging through every image in
    // the current view.
    if (item.mime_type?.startsWith("image/")) {
      const sourceList =
        currentView === "shared"
          ? shareAccessItem
          : currentView === "official"
            ? officialDocuments
            : quickAccessItems;

      const images = sourceList.filter(
        (i) => i.type !== 1 && i.mime_type?.startsWith("image/"),
      );
      const startIndex = Math.max(
        0,
        images.findIndex((i) => i.id === item.id),
      );
      setLightbox({ show: true, images, index: startIndex });
      return;
    }

    if (isPreviewable(item)) {
      setPreviewModal({ show: true, item });
    } else {
      const fileUrl = `${process.env.REACT_APP_API_BASE_URL}/Uploads/${item.path}`;
      window.open(fileUrl, "_blank");
    }
  };

  const handleItemClick = (item: DriveItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (e.ctrlKey) {
      setSelectedItems((prev) =>
        prev.some((sel) => sel.id === item.id)
          ? prev.filter((sel) => sel.id !== item.id)
          : [...prev, item],
      );
    } else {
      // Single select
      setSelectedItems([item]);
    }
  };

  const handleCut = () => {
    if (selectedItems.length > 0 && currentView === "myfolder") {
      setCutItems(selectedItems);
      setSelectedItems([]); // Clear selection after cut
      showBasicAlert(`${selectedItems.length} item(s) cut to clipboard.`);
    } else {
      showBasicAlert("Select items in My Folder to cut.");
    }
  };

  const handlePaste = async () => {
    if (
      cutItems.length === 0 ||
      // currentFolderId === null ||
      currentView !== "myfolder"
    ) {
      showBasicAlert("No items to paste or invalid target.");
      return;
    }

    try {
      const data = {
        signature,
        source_ids: cutItems.map((item) => item.id),
        target_folder_id: currentFolderId,
      };

      const response = await api.post("driveService/moveDriveItem", data);
      if (!response.data?.message?.[0]?.status) {
        throw new Error("Move failed for items.");
      }

      showBasicAlert("Items pasted successfully.");
      setCutItems([]); // Clear cut items
      await fetchCurrentFolderItems(currentFolderId);
    } catch (error: any) {
      console.error("Paste error:", error);
      showBasicAlert(error.message || "Failed to paste items.");
    }
  };

  const handleContextMenuItem = (e: React.MouseEvent, item: DriveItem) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedItems.some((sel) => sel.id === item.id)) {
      setSelectedItems([item]);
    }
    setContextMenu({ x: e.pageX, y: e.pageY, type: "item", targetItem: item });
  };

  const handleContextMenuFolder = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.pageX, y: e.pageY, type: "folder" });
  };

  const handleCreateFolderClick = () => {
    (
      document.querySelector('[data-bs-target="#add_folder"]') as HTMLElement
    )?.click();
    setContextMenu(null);
  };

  const handleDragStart = (e: React.DragEvent, item: DriveItem) => {
    e.stopPropagation();

    // If the dragged item is not selected, select it
    if (!selectedItems.some((sel) => sel.id === item.id)) {
      setSelectedItems([item]);
    }

    // Set data for the drag operation
    const data = JSON.stringify({
      type: "fileManagerItems",
      items: selectedItems.length > 0 ? selectedItems : [item],
    });

    e.dataTransfer.setData("application/json", data);
    e.dataTransfer.effectAllowed = "move";
    setIsDragging(true);
  };

  // Handle drag over for folders and files
  const handleDragOver = (e: React.DragEvent, item?: DriveItem) => {
    e.preventDefault();
    e.stopPropagation();

    // Files dragged in from the OS
    if (e.dataTransfer.types?.includes("Files")) {
      e.dataTransfer.dropEffect = "copy";
      setIsFileDragging(true);
      if (item && item.type === 1) {
        setDragOverItem(item.id);
        setDragOverFolder(true);
      }
      return;
    }

    e.dataTransfer.dropEffect = "move";

    if (item && item.type === 1) {
      // If over a folder
      setDragOverItem(item.id);
      setDragOverFolder(true);
    } else if (!item) {
      // If over the main area
      setDragOverItem(null);
      setDragOverFolder(false);
    }
  };

  // Handle drag leave
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverItem(null);
    setDragOverFolder(false);
    setIsFileDragging(false);
  };

  // Handle drop operation
  const handleDrop = async (e: React.DragEvent, targetFolder?: DriveItem) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);
    setDragOverItem(null);
    setDragOverFolder(false);
    setIsFileDragging(false);

    try {
      // Files dragged in from the OS -> upload them
      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles && droppedFiles.length > 0) {
        const uploadParentId =
          targetFolder && targetFolder.type === 1
            ? targetFolder.id
            : currentFolderId;

        for (const file of Array.from(droppedFiles)) {
          await handleUpload(file, uploadParentId);
        }
        return;
      }

      // Get the dragged items data
      const data = e.dataTransfer.getData("application/json");
      if (!data) return;

      const { type, items } = JSON.parse(data);
      if (type !== "fileManagerItems" || !items || items.length === 0) return;

      // Determine target folder ID
      const targetFolderId = targetFolder ? targetFolder.id : currentFolderId;

      // Prepare API call data
      const moveData = {
        signature,
        source_ids: items.map((item: DriveItem) => item.id),
        target_folder_id: targetFolderId,
      };

      const response = await api.post("driveService/moveDriveItem", moveData);

      if (response.data?.message?.[0]?.status) {
        showBasicAlert("Items moved successfully.");
        // Refresh the current folder view
        await fetchCurrentFolderItems(currentFolderId);
        setSelectedItems([]);
      } else {
        throw new Error("Move operation failed");
      }
    } catch (error: any) {
      console.error("Move error:", error);
      showBasicAlert(error.message || "Failed to move items.");
    }
  };

  const sortState: SortState = {
    column: sortColumn,
    direction: sortDirection,
    onSort: handleSort,
  };

  const editState: EditState = {
    editingItemId,
    editName,
    setEditName,
    onKeyDown: handleKeyDown,
    onBlur: handleBlur,
  };

  const selectionState: SelectionState = {
    selectedItems,
    onItemClick: handleItemClick,
    onContextMenu: handleContextMenuItem,
  };

  const dragState: DragState = {
    isDragging,
    dragOverItem,
    onDragStart: handleDragStart,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  };

  const fullActions: FileActionsConfig = {
    variant: "full",
    onRename: handleEdit,
    onDelete: handleDelete,
    onDownload: handleDownloadFile,
    onShare: (item) => {
      setSelectedId(item);
      setShowShareModal(true);
      setStep(1);
    },
    onShareInfo: handleShowShareDetails,
    onCopyLink: handleCopy,
  };

  const readonlyActions: FileActionsConfig = {
    variant: "readonly",
    onDownload: handleDownloadFile,
    onCopyLink: handleCopy,
  };

  const friendlyType = (item: DriveItem) => getFriendlyFileType(item.mime_type);
  const officialType = (item: DriveItem) =>
    item.document_type || getFriendlyFileType(item.mime_type) || "File";
  const sizeIfPositive = (item: DriveItem) =>
    Number(item.size) > 0 ? formatSize(Number(item.size)) : "-";
  const sizeByType = (item: DriveItem) =>
    item.type === 1 ? "-" : formatSize(Number(item.size));
  const dateUpdated = (item: DriveItem) =>
    item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : "N/A";
  const dateShared = (item: any) =>
    item.share_details?.shared_at
      ? new Date(item.share_details.shared_at).toLocaleDateString()
      : "N/A";

  const renderSharedBy = (item: any) =>
    item.share_details?.shared_by ? (
      <div className="d-flex align-items-center gap-2">
        <img
          src={item.share_details.shared_by.profile_pic}
          alt={item.share_details.shared_by.name}
          width={32}
          style={{ borderRadius: "50%", objectFit: "cover" }}
        />
        <span>{item.share_details.shared_by.name}</span>
      </div>
    ) : (
      "N/A"
    );

  const myFolderListRows = sortedItems(quickAccessItems).slice(
    0,
    visibleCounts.quickAccess,
  );
  const myFolderGridRows = [...quickAccessItems]
    .sort((a, b) => {
      if (a.type === 1 && b.type !== 1) return -1;
      if (a.type !== 1 && b.type === 1) return 1;
      return a.name.localeCompare(b.name);
    })
    .slice(0, visibleCounts.quickAccess);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">🗂️ File Manager</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Application</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    File Manager
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="d-flex align-items-center border rounded p-1 me-2">
                <button
                  className={`btn btn-icon btn-sm me-1 ${
                    viewMode === "grid" ? "bg-primary text-white" : ""
                  }`}
                  onClick={() => setViewMode("grid")}
                  title="Grid View"
                >
                  <i className="ti ti-layout-grid" />
                </button>
                <button
                  className={`btn btn-icon btn-sm ${
                    viewMode === "list" ? "bg-primary text-white" : ""
                  }`}
                  onClick={() => setViewMode("list")}
                  title="List View"
                >
                  <i className="ti ti-list-details" />
                </button>
              </div>

              <div className="d-flex align-items-center me-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                {showAllFolders && (
                  <button
                    type="button"
                    onClick={handleNewClick}
                    className="btn btn-primary d-flex align-items-center me-2"
                  >
                    <i className="ti ti-circle-plus me-2" />
                    New
                  </button>
                )}
                {showAllFolders && (
                  <Link
                    to="#"
                    data-bs-toggle="modal"
                    data-inert={true}
                    data-bs-target="#add_folder"
                    className="btn btn-primary d-flex align-items-center me-2"
                  >
                    <i className="ti ti-circle-plus me-2" />
                    Create Folder
                  </Link>
                )}
              </div>
              <div className="d-flex align-items-center me-2">
                <CollapseHeader />
              </div>
            </div>
          </div>

          <div className="row">
            {showAllFolders && (
              <div
                className={`col-xl-${panelSize} position-relative`}
                onClick={(e) => {
                  if (!e.ctrlKey) setSelectedItems([]);
                }}
                onContextMenu={handleContextMenuFolder}
                onDragOver={(e) => handleDragOver(e)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e)}
                style={{
                  backgroundColor:
                    (isDragging || isFileDragging) && !dragOverFolder
                      ? "#f0f8ff"
                      : "transparent",
                  border:
                    isDragging || isFileDragging ? "2px dashed #007bff" : "none",
                  borderRadius: isDragging || isFileDragging ? "5px" : "0",
                  minHeight: isDragging || isFileDragging ? "200px" : "auto",
                }}
              >
                <div className="d-flex justify-content-start mb-3">
                  {folderStack.length !== 0 && (
                    <button
                      className="btn btn-primary d-flex align-items-center me-2"
                      onClick={handleBack}
                    >
                      <i className="ti ti-arrow-left me-2"></i> Back
                    </button>
                  )}
                </div>

                <div
                  className={
                    viewMode === "grid"
                      ? "row row-cols-xxl-5 row-cols-xl-3 row-cols-sm-3 row-cols-1 justify-content-center"
                      : "file-list-table"
                  }
                >
                  {quickAccessItems.length === 0 ? (
                    <div className="col text-center mt-4">
                      <p className="text-muted">This folder is empty.</p>
                    </div>
                  ) : (
                    <>
                      {viewMode === "list" ? (
                        <FileListView
                          rows={myFolderListRows}
                          scroll={quickAccessItems.length > 10}
                          sort={sortState}
                          onItemDoubleClick={handleFileDoubleClick}
                          actions={fullActions}
                          getType={friendlyType}
                          getSize={sizeIfPositive}
                          getDate={dateUpdated}
                          truncateNameAt={12}
                          edit={editState}
                          selection={selectionState}
                          drag={dragState}
                        />
                      ) : (
                        <FileGridView
                          variant="card"
                          rows={myFolderGridRows}
                          scroll={quickAccessItems.length > 10}
                          onItemDoubleClick={handleFileDoubleClick}
                          actions={fullActions}
                          edit={editState}
                          selection={selectionState}
                          drag={dragState}
                        />
                      )}
                      <LoadMoreToggle
                        visible={visibleCounts.quickAccess}
                        total={totalItems}
                        onChange={(next) =>
                          setVisibleCounts((prev: any) => ({
                            ...prev,
                            quickAccess: next,
                          }))
                        }
                      />
                    </>
                  )}
                </div>

                {showShareModal && (
                  <div
                    className="modal fade show d-block"
                    tabIndex={-1}
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                  >
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h4 className="modal-title">Share File</h4>
                          <button
                            type="button"
                            className="btn-close custom-btn-close"
                            onClick={() => setShowShareModal(false)}
                            aria-label="Close"
                          >
                            <i className="ti ti-x" />
                          </button>
                        </div>
                        <form onSubmit={(e) => e.preventDefault()}>
                          <div className="modal-body">
                            {step === 1 && (
                              <>
                                <label className="form-label">
                                  Select Company
                                </label>
                                <CommonSelect
                                  className="select"
                                  options={companyChoose}
                                  defaultValue={undefined}
                                  onChange={handleCompanyChange}
                                />
                              </>
                            )}
                            {step === 2 && (
                              <>
                                <p>
                                  <strong>Company:</strong>{" "}
                                  {selectedCompany.companyName} (
                                  {selectedCompany.companyType})
                                </p>
                                <label className="form-label">
                                  Select Employee
                                </label>
                                <ImageSelectDropMenu
                                  className="select"
                                  options={filteredEmployees.map((emp) => ({
                                    label: `${emp.Name} - ${emp.Email}`,
                                    value: emp.key,
                                    imageUrl: getEmployeeImageUrl(
                                      emp.key,
                                      employeeDetail,
                                      currentUser,
                                    ),
                                  }))}
                                  defaultValue={undefined}
                                  placeHolder="Select Employee"
                                  onChange={(selected: any) =>
                                    setSelectedEmployee(
                                      selected ? selected.value : null,
                                    )
                                  }
                                />

                                <div className="mt-3 text-end">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    disabled={!selectedEmployee}
                                    onClick={() =>
                                      handleShare(selectedEmployee)
                                    }
                                  >
                                    Share
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                          <div className="modal-footer">
                            <button
                              type="button"
                              className="btn btn-light me-2"
                              onClick={() => setShowShareModal(false)}
                            >
                              Cancel
                            </button>
                            {step === 1 && (
                              <button
                                type="button"
                                className="btn btn-primary"
                                disabled={!selectedCompany.companyId}
                                onClick={() => setStep(2)}
                              >
                                Next
                              </button>
                            )}
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {showSharedWithMe && (
              <div className="col-xl-9 position-relative">
                <div
                  className={
                    viewMode === "grid"
                      ? "row row-cols-xxl-5 row-cols-xl-3 row-cols-sm-3 row-cols-1 justify-content-center"
                      : "file-list-table"
                  }
                >
                  {shareAccessItem.length > 0 ? (
                    <>
                      {viewMode === "list" ? (
                        <FileListView
                          rows={sortedItems(shareAccessItem)}
                          scroll={shareAccessItem.length > 10}
                          sort={sortState}
                          onItemDoubleClick={handleFileDoubleClick}
                          actions={readonlyActions}
                          getType={friendlyType}
                          getSize={sizeByType}
                          getDate={dateShared}
                          dateLabel="Date Shared"
                          showSharedBy
                          renderSharedBy={renderSharedBy}
                        />
                      ) : (
                        <FileGridView
                          variant="thumb"
                          rows={shareAccessItem}
                          scroll={shareAccessItem.length > 10}
                          onItemDoubleClick={handleFileDoubleClick}
                          actions={readonlyActions}
                        />
                      )}
                      <LoadMoreToggle
                        visible={visibleCounts.shared}
                        total={totalSharedItems}
                        onChange={(next) =>
                          setVisibleCounts((prev: any) => ({
                            ...prev,
                            shared: next,
                          }))
                        }
                      />
                    </>
                  ) : (
                    <div className="text-center">No shared files found.</div>
                  )}
                </div>
                <div className="row-bottom position-absolute bottom-0"></div>
              </div>
            )}

            {showOfficialDocuments && (
              <div className="col-xl-9 position-relative">
                <div
                  className={
                    viewMode === "grid"
                      ? "row row-cols-xxl-5 row-cols-xl-3 row-cols-sm-3 row-cols-1 justify-content-center"
                      : "file-list-table"
                  }
                >
                  {officialDocuments.length > 0 ? (
                    <>
                      {viewMode === "list" ? (
                        <FileListView
                          rows={sortedItems(officialDocuments)}
                          scroll={officialDocuments.length > 10}
                          sort={sortState}
                          onItemDoubleClick={handleFileDoubleClick}
                          actions={readonlyActions}
                          getType={officialType}
                          getSize={sizeByType}
                          getDate={dateUpdated}
                          edit={editState}
                        />
                      ) : (
                        <FileGridView
                          variant="thumb"
                          rows={sortedItems(officialDocuments)}
                          scroll={officialDocuments.length > 10}
                          onItemDoubleClick={handleFileDoubleClick}
                          actions={readonlyActions}
                          edit={editState}
                        />
                      )}
                      <LoadMoreToggle
                        visible={visibleCounts.official}
                        total={totalOfficialItems}
                        onChange={(next) =>
                          setVisibleCounts((prev: any) => ({
                            ...prev,
                            official: next,
                          }))
                        }
                      />
                    </>
                  ) : (
                    <div className="text-center">
                      No official documents found.
                    </div>
                  )}
                </div>
                <div className="row-bottom"></div>
              </div>
            )}

            {showRecycleBin && (
              <div className="col-xl-9 position-relative">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="mb-0 d-flex align-items-center">
                    <i className="ti ti-trash me-2" /> Recycle Bin
                  </h5>
                  <span className="text-muted fs-12">
                    Items are automatically deleted 30 days after removal.
                  </span>
                </div>
                <div className="file-list-table">
                  <RecycleBinView
                    rows={recycleBinItems}
                    scroll={recycleBinItems.length > 10}
                    onRestore={handleRestore}
                    onPermanentDelete={handlePermanentDelete}
                  />
                </div>
              </div>
            )}
            <div className="col-xl-3 theiaStickySidebar">
              <div className="sticky-class">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center">
                      <ImageWithBasePath
                        src="assets/img/icons/storage.svg"
                        alt="img"
                      />
                      <h5 className="ms-2">CMS-DRIVE</h5>
                    </div>
                  </div>
                  <div className="progress progress-xs flex-grow-1 mb-2">
                    <div
                      className="progress-bar bg-purple rounded"
                      role="progressbar"
                      style={{ width: `${usagePercent.toFixed(2)}%` }}
                      aria-valuenow={usagePercent}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>
                  <div className="d-flex align-items-center justify-content-between">
                    <p className="text-title mb-0">
                      {formatSize(storageInfo.used_storage)} /{" "}
                      {formatSize(storageInfo.max_storage)}
                    </p>
                  </div>
                </div>

                <div className="card">
                  <div className="card-body p-3">
                    <div className="shadow-xs p-2 mb-3">
                      <div className="d-flex align-items-center justify-content-between"></div>
                    </div>
                    {showAllFolders && (
                      <div
                        className="file-drop mb-3 text-center"
                        onClick={handleNewClick}
                        onDragOver={(e) => handleDragOver(e)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e)}
                        style={{
                          cursor: "pointer",
                          backgroundColor: isFileDragging
                            ? "#f0f8ff"
                            : undefined,
                          border: isFileDragging
                            ? "2px dashed #007bff"
                            : undefined,
                          borderRadius: isFileDragging ? "5px" : undefined,
                        }}
                      >
                        <span className="avatar avatar-sm bg-primary text-white mb-2">
                          <i className="ti ti-upload fs-16" />
                        </span>
                        <h6 className="mb-2">Drop files here</h6>
                        <p className="fs-12 mb-0">
                          Browse and choose the files you want to upload from
                          your computer
                        </p>
                        {selectedFile && (
                          <div className="mt-2 text-success fw-medium">
                            Selected: {selectedFile.name}
                            {uploading && (
                              <span className="text-muted ms-2">
                                (Uploading...)
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="files-list nav d-block">
                      <Link
                        to="#"
                        className={`d-flex align-items-center fw-medium p-2 ${
                          showAllFolders ? "active" : ""
                        }`}
                        onClick={() => {
                          setShowAllFolders(true);
                          setShowSharedWithMe(false);
                          setShowOfficialDocuments(false);
                          setShowRecycleBin(false);
                          setCurrentView("myfolder");
                          setActiveFileType(null);
                          setQuickAccessItems(originalQuickAccessItems);
                        }}
                      >
                        <i className="ti ti-folder-up me-2" />
                        All Folder / Files
                      </Link>
                      <Link
                        to="#"
                        className={`d-flex align-items-center fw-medium p-2 ${
                          showSharedWithMe ? "active" : ""
                        }`}
                        onClick={() => {
                          setShowSharedWithMe(true);
                          setShowAllFolders(false);
                          setShowOfficialDocuments(false);
                          setShowRecycleBin(false);
                          setCurrentView("shared");
                          setActiveFileType(null);
                          setShareAccessItem(
                            originalShareAccessItems.map((item) => ({
                              ...(item as any).file,
                              access_type: (item as any).access_type,
                              share_details: (item as any).share_details,
                            })),
                          );
                        }}
                      >
                        <i className="ti ti-folder-up me-2" />
                        Shared with me
                      </Link>
                      <Link
                        to="#"
                        className={`d-flex align-items-center fw-medium p-2 ${
                          showOfficialDocuments ? "active" : ""
                        }`}
                        onClick={() => {
                          setShowOfficialDocuments(true);
                          setShowAllFolders(false);
                          setShowSharedWithMe(false);
                          setShowRecycleBin(false);
                          setCurrentView("official");
                          setActiveFileType(null);
                          setOfficialDocuments(originalOfficialDocuments);
                        }}
                      >
                        <i className="ti ti-folder-up me-2" />
                        Official Document
                      </Link>
                      <Link
                        to="#"
                        className={`d-flex align-items-center fw-medium p-2 ${
                          showRecycleBin ? "active" : ""
                        }`}
                        onClick={() => {
                          setShowRecycleBin(true);
                          setShowAllFolders(false);
                          setShowSharedWithMe(false);
                          setShowOfficialDocuments(false);
                          setActiveFileType(null);
                          fetchRecycleBinItems();
                        }}
                      >
                        <i className="ti ti-trash me-2" />
                        Recycle Bin
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="card mt-3">
                  <div className="card-header d-flex align-items-center justify-content-between">
                    <h5 className="card-title mb-0 d-flex align-items-center">
                      {getSectionTitle()}
                    </h5>
                  </div>

                  <div className="card-body p-0">
                    <ul className="list-group list-group-flush">
                      <li
                        className={`list-group-item d-flex align-items-center justify-content-between ${
                          activeFileType === "all" ? "active" : ""
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveFileType("all");

                          if (currentView === "myfolder") {
                            setQuickAccessItems(originalQuickAccessItems);
                            setFileCounts((prev) => ({
                              ...prev,
                              myfolder: prev.myfolder,
                            }));
                          } else if (currentView === "shared") {
                            setShareAccessItem(
                              originalShareAccessItems.map((item) => ({
                                ...(item as any).file,
                                access_type: (item as any).access_type,
                                share_details: (item as any).share_details,
                              })),
                            );

                            setFileCounts((prev) => ({
                              ...prev,
                              shared: prev.shared,
                            }));
                          } else if (currentView === "official") {
                            setOfficialDocuments(originalOfficialDocuments);
                            setFileCounts((prev) => ({
                              ...prev,
                              official: prev.official,
                            }));
                          }
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <span className="avatar avatar-xs bg-pink text-white me-2">
                            <i className="ti ti-filter" />
                          </span>
                          <span>All</span>
                        </div>
                      </li>
                      <li
                        className={`list-group-item d-flex align-items-center justify-content-between ${
                          activeFileType === "folder" ? "active" : ""
                        }`}
                        onClick={() => handleFileTypeClick("folder")}
                      >
                        <div className="d-flex align-items-center">
                          <span className="avatar avatar-xs bg-pink text-white me-2">
                            <i className="ti ti-folder" />
                          </span>
                          <span>Folder</span>
                        </div>
                        <span className="badge bg-pink-light text-pink">
                          {currentFileTypeCount?.folder || 0}
                        </span>
                      </li>
                      <li
                        className={`list-group-item d-flex align-items-center justify-content-between ${
                          activeFileType === "image" ? "active" : ""
                        }`}
                        onClick={() => handleFileTypeClick("image")}
                      >
                        <div className="d-flex align-items-center">
                          <span className="avatar avatar-xs bg-pink text-white me-2">
                            <i className="ti ti-photo" />
                          </span>
                          <span>Images</span>
                        </div>
                        <span className="badge bg-pink-light text-pink">
                          {currentFileTypeCount?.image || 0}
                        </span>
                      </li>
                      <li
                        className={`list-group-item d-flex align-items-center justify-content-between ${
                          activeFileType === "video" ? "active" : ""
                        }`}
                        onClick={() => handleFileTypeClick("video")}
                      >
                        <div className="d-flex align-items-center">
                          <span className="avatar avatar-xs bg-purple text-white me-2">
                            <i className="ti ti-video" />
                          </span>
                          <span>Videos</span>
                        </div>
                        <span className="badge bg-purple-light text-purple">
                          {currentFileTypeCount?.video || 0}
                        </span>
                      </li>
                      <li
                        className={`list-group-item d-flex align-items-center justify-content-between ${
                          activeFileType === "pdf" ? "active" : ""
                        }`}
                        onClick={() => handleFileTypeClick("pdf")}
                      >
                        <div className="d-flex align-items-center">
                          <span className="avatar avatar-xs bg-danger text-white me-2">
                            <i className="ti ti-file-text" />
                          </span>
                          <span>PDFs</span>
                        </div>
                        <span className="badge bg-danger-light text-danger">
                          {currentFileTypeCount?.pdf || 0}
                        </span>
                      </li>
                      <li
                        className={`list-group-item d-flex align-items-center justify-content-between ${
                          activeFileType === "other" ? "active" : ""
                        }`}
                        onClick={() => handleFileTypeClick("other")}
                      >
                        <div className="d-flex align-items-center">
                          <span className="avatar avatar-xs bg-info text-white me-2">
                            <i className="ti ti-file" />
                          </span>
                          <span>Other Files</span>
                        </div>
                        <span className="badge bg-info-light text-info">
                          {currentFileTypeCount?.other || 0}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              ZYVO
            </Link>
          </p>
        </div>
      </div>
      {showAllFolders && (
        <FileModal
          currentFolderId={currentFolderId}
          setQuickAccessItems={setQuickAccessItems}
          setFileCounts={setFileCounts}
        />
      )}

      {/* File Preview Modal */}
      {previewModal.show && previewModal.item && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.75)" }}
          onClick={() => setPreviewModal({ show: false, item: null })}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-content border-0 shadow-lg"
              style={{ borderRadius: 14, overflow: "hidden" }}
            >
              <div className="modal-header py-2 px-3 align-items-center">
                <div
                  className="d-flex align-items-center gap-2"
                  style={{ minWidth: 0 }}
                >
                  <ImageWithBasePath
                    src={`assets/img/icons/${getIconByMime(previewModal.item)}`}
                    alt={previewModal.item.name}
                    width={22}
                    height={22}
                  />
                  <div style={{ minWidth: 0 }}>
                    <h6 className="mb-0 text-truncate" title={previewModal.item.name}>
                      {previewModal.item.name}
                    </h6>
                    <small className="text-muted">
                      {getFriendlyFileType(previewModal.item.mime_type)}
                      {Number(previewModal.item.size) > 0
                        ? ` · ${formatSize(Number(previewModal.item.size))}`
                        : ""}
                    </small>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 ms-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary d-inline-flex align-items-center"
                    onClick={() => handleDownloadFile(previewModal.item!)}
                  >
                    <i className="ti ti-download me-1" /> Download
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-light"
                    onClick={() => setPreviewModal({ show: false, item: null })}
                    aria-label="Close"
                  >
                    <i className="ti ti-x" />
                  </button>
                </div>
              </div>
              <div
                className="modal-body p-0 d-flex justify-content-center align-items-center"
                style={{
                  background:
                    "repeating-conic-gradient(#f1f1f4 0% 25%, #e7e7ee 0% 50%) 50% / 24px 24px",
                  minHeight: "60vh",
                  maxHeight: "82vh",
                  overflow: "auto",
                }}
              >
                <FilePreview
                  item={previewModal.item}
                  variant="modal"
                  onDownload={handleDownloadFile}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Picasa-style image viewer */}
      {lightbox.show && lightbox.images.length > 0 && (
        <ImageLightbox
          images={lightbox.images}
          startIndex={lightbox.index}
          onClose={() => setLightbox({ show: false, images: [], index: 0 })}
          onDownload={handleDownloadFile}
        />
      )}

      {/* Custom Context Menu */}
      {contextMenu && (
        <div
          style={{
            position: "absolute",
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
            zIndex: 1000,
          }}
        >
          <ul className="dropdown-menu show p-2">
            {contextMenu.type === "item" && contextMenu.targetItem && (
              <>
                <li>
                  <Link
                    to="#"
                    data-bs-toggle="modal"
                    data-inert={true}
                    data-bs-target="#delete_file_modal"
                    className="dropdown-item rounded-1"
                    onClick={() => {
                      handleDelete(selectedItems);
                      setContextMenu(null);
                    }}
                  >
                    <i className="ti ti-trash me-1" /> Delete
                  </Link>
                </li>
                {contextMenu.targetItem.type !== 1 && (
                  <>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        onClick={() => {
                          handleDownloadFile(contextMenu.targetItem!);
                          setContextMenu(null);
                        }}
                      >
                        <i className="ti ti-download me-1" /> Download
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        onClick={() => {
                          setSelectedId(contextMenu.targetItem!);
                          setShowShareModal(true);
                          setStep(1);
                          setContextMenu(null);
                        }}
                      >
                        <i className="ti ti-share me-1" /> Share
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        onClick={() => {
                          handleCopy(contextMenu.targetItem!);
                          setContextMenu(null);
                        }}
                      >
                        <i className="ti ti-link me-1" /> Copy Link
                      </Link>
                    </li>
                  </>
                )}
                <li>
                  <Link
                    to="#"
                    className="dropdown-item rounded-1"
                    onClick={() => {
                      handleCut();
                      setContextMenu(null);
                    }}
                  >
                    <i className="ti ti-scissors me-1" /> Move to
                  </Link>
                </li>
              </>
            )}
            {contextMenu.type === "folder" && (
              <>
                {cutItems.length > 0 && (
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={() => {
                        handlePaste();
                        setContextMenu(null);
                      }}
                    >
                      <i className="ti ti-paste me-1" /> Move here
                    </Link>
                  </li>
                )}
                <li>
                  <Link
                    to="#"
                    className="dropdown-item rounded-1"
                    onClick={() => {
                      handleNewClick();
                      setContextMenu(null);
                    }}
                  >
                    <i className="ti ti-upload me-1" /> Upload File
                  </Link>
                </li>
                <li>
                  <Link
                    to="#"
                    className="dropdown-item rounded-1"
                    onClick={() => {
                      handleCreateFolderClick();
                      setContextMenu(null);
                    }}
                  >
                    <i className="ti ti-folder-plus me-1" /> Create Folder
                  </Link>
                </li>
              </>
            )}
          </ul>
          {isDragging && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.1)",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  padding: "20px",
                  backgroundColor: "white",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                }}
              >
                <i className="ti ti-hand-click me-2" />
                Drop files here to move them
              </div>
            </div>
          )}
        </div>
      )}
      <DeleteModal
        modalId="delete_file_modal"
        onDelete={handleConfirmDelete}
        title="Delete Item(s)"
        message={
          itemsToDelete.length === 1
            ? `Are you sure you want to delete "${itemsToDelete[0]?.name}"? This action cannot be undone.`
            : `Are you sure you want to delete ${itemsToDelete.length} items? This action cannot be undone.`
        }
      />
      <DeleteModal
        modalId="purge_file_modal"
        onDelete={handleConfirmPurge}
        title="Delete Permanently"
        message={
          itemsToPurge.length === 1
            ? `Permanently delete "${itemsToPurge[0]?.name}"? This cannot be undone and the file will be removed from the server.`
            : `Permanently delete ${itemsToPurge.length} items? This cannot be undone and the files will be removed from the server.`
        }
      />
      {showShareDetailsModal &&
        selectedFileForShareDetails &&
        selectedFileShareInfo && (
          <ShareDetailsModal
            show={showShareDetailsModal}
            onClose={() => {
              setShowShareDetailsModal(false);
              setSelectedFileForShareDetails(null);
              setSelectedFileShareInfo(null);
            }}
            file={selectedFileForShareDetails}
            shareInfo={selectedFileShareInfo}
          />
        )}
    </>
  );
};

export default FileManager;
